import { Checkbox, Disclosure } from "@design";
import { useTranslation } from "react-i18next";
import type { ReasoningConfig } from "@/api";
import { withForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { Hint } from "./Hint";
import { LabeledSelect } from "./LabeledSelect";
import { emptyModelValues } from "./modelForm";
import { CAPABILITY_KEYS, MODEL_TYPES, REASONING_EFFORTS, toggleEffort } from "./modelOptions";

const NOT_SET = "";

function ReasoningEfforts({
  value,
  onChange,
}: Readonly<{ value: ReasoningConfig | null; onChange: (value: ReasoningConfig | null) => void }>) {
  const { t } = useTranslation("adminModels");
  const supported = value?.supportedEfforts ?? [];
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1">
        <Text as="span" variant="body-sm">
          {t("form.reasoning.title")}
        </Text>
      </legend>
      <Hint>{t("form.reasoning.hint")}</Hint>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {REASONING_EFFORTS.map((effort) => (
          <Checkbox
            key={effort}
            label={t(`efforts.${effort}`)}
            checked={supported.includes(effort)}
            onChange={(checked) => onChange(toggleEffort(value, effort, checked))}
          />
        ))}
      </div>
      {value && supported.length > 0 && (
        <LabeledSelect
          label={t("form.reasoning.default")}
          value={value.defaultEffort ?? NOT_SET}
          options={[
            { value: NOT_SET, label: t("form.reasoning.notSet") },
            ...supported.map((effort) => ({ value: effort, label: t(`efforts.${effort}`) })),
          ]}
          onChange={(next) =>
            onChange({
              supportedEfforts: supported,
              defaultEffort: next === NOT_SET ? null : (next as ReasoningConfig["supportedEfforts"][number]),
            })
          }
        />
      )}
    </fieldset>
  );
}

/** Collapsed "Advanced": model type, behavior flags and reasoning efforts. */
export const AdvancedFields = withForm({
  defaultValues: emptyModelValues(),
  render: function AdvancedFieldsRender({ form }) {
    const { t } = useTranslation("adminModels");
    return (
      <Disclosure label={t("form.advanced.title")}>
        <div className="flex flex-col gap-5 p-4">
          <form.Field name="modelType">
            {(field) => (
              <LabeledSelect
                label={t("form.modelType")}
                value={field.state.value}
                options={MODEL_TYPES.map((value) => ({ value, label: t(`modelTypes.${value}`) }))}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-1">
              <Text as="span" variant="body-sm">
                {t("form.capabilities.title")}
              </Text>
            </legend>
            <Hint>{t("form.capabilities.hint")}</Hint>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {CAPABILITY_KEYS.map((key) => (
                <form.Field key={key} name={key}>
                  {(field) => (
                    <Checkbox
                      label={t(`capabilities.${key}.label`)}
                      description={t(`capabilities.${key}.description`)}
                      checked={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
              ))}
            </div>
          </fieldset>
          <form.Subscribe selector={(s) => s.values.isReasoning}>
            {(isReasoning) =>
              isReasoning ? (
                <form.Field name="reasoningConfig">
                  {(field) => <ReasoningEfforts value={field.state.value} onChange={field.handleChange} />}
                </form.Field>
              ) : (
                <Hint>{t("form.reasoning.enableFirst")}</Hint>
              )
            }
          </form.Subscribe>
        </div>
      </Disclosure>
    );
  },
});
